import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Paper, Typography, ToggleButton, ToggleButtonGroup,
  CircularProgress, useTheme, IconButton, Tooltip,
} from '@mui/material';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import api from '../utils/api';

const fmtTs = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * 逐字稿清單本體（含自動捲動）。抽成獨立元件是為了能同時掛載在
 * 主面板和子母畫面視窗（各自需要獨立的 listRef / itemRefs，不能共用）。
 */
function TranscriptList({ segments, activeIdx, lang, onSeek, sx }) {
  const theme = useTheme();
  const listRef = useRef(null);
  const itemRefs = useRef({});

  useEffect(() => {
    if (activeIdx < 0) return;
    const container = listRef.current;
    const el = itemRefs.current[activeIdx];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const offsetInContainer = (eRect.top - cRect.top) + container.scrollTop;
    const target = offsetInContainer - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top: target, behavior: 'smooth' });
  }, [activeIdx]);

  return (
    <Box
      ref={listRef}
      sx={{
        position: 'relative',
        maxHeight: 480,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        p: 1,
        ...sx,
      }}
    >
      {segments.map((s, i) => {
        const active = i === activeIdx;
        return (
          <Box
            key={i}
            ref={(el) => { itemRefs.current[i] = el; }}
            onClick={() => onSeek(s.start_ms)}
            sx={{
              px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer',
              display: 'flex', gap: 1.5,
              bgcolor: active ? 'action.selected' : 'transparent',
              borderLeft: active ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
              '&:hover': { bgcolor: 'action.hover' },
              transition: 'background-color 0.15s',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', minWidth: 40, pt: 0.3, userSelect: 'none' }}
            >
              {fmtTs(s.start_ms)}
            </Typography>
            <Box>
              {(lang === 'ja' || lang === 'both') && (
                <Typography
                  variant="body2"
                  sx={{ color: lang === 'both' ? 'text.secondary' : 'text.primary' }}
                >
                  {s.ja}
                </Typography>
              )}
              {(lang === 'zh' || lang === 'both') && (
                <Typography variant="body1" sx={{ fontWeight: active ? 600 : 400 }}>
                  {s.zh}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const PIP_WINDOW_RADIUS = 3; // 動畫捲動時，視窗外多渲染幾列，捲動過程才看得到文字滑過去

/**
 * 子母畫面用的精簡字幕顯示：像跑馬燈/字幕機一樣，當前句置中、
 * 隨播放進度平滑往上捲動。長句子直接換成多行顯示，不用省略號截斷——
 * 因此每列高度不固定，改用實際量測到的 DOM 高度（offsetTop/offsetHeight）
 * 算捲動位移，而不是像固定列高那樣直接用索引乘列高。
 */
function PipCaption({ segments, activeIdx, lang, onSeek }) {
  const viewportRef = useRef(null);
  const rowRefs = useRef({});
  const [translateY, setTranslateY] = useState(0);

  useLayoutEffect(() => {
    const currentEl = rowRefs.current[activeIdx];
    const viewport = viewportRef.current;
    if (!currentEl || !viewport) return;
    setTranslateY(viewport.clientHeight / 2 - currentEl.offsetTop - currentEl.offsetHeight / 2);
  });

  if (activeIdx < 0) return <Box sx={{ flex: 1 }} />;

  const windowStart = Math.max(0, activeIdx - PIP_WINDOW_RADIUS);
  const windowEnd = Math.min(segments.length - 1, activeIdx + PIP_WINDOW_RADIUS);
  const windowSegs = segments.slice(windowStart, windowEnd + 1);

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 2, overflow: 'hidden' }}>
      <Box ref={viewportRef} sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0,
          transform: `translateY(${translateY}px)`,
          transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        >
          {windowSegs.map((seg, i) => {
            const idx = windowStart + i;
            const distance = idx - activeIdx;
            const emphasis = distance === 0;
            return (
              <Box
                key={idx}
                ref={(el) => { rowRefs.current[idx] = el; }}
                onClick={() => onSeek(seg.start_ms)}
                sx={{
                  py: 0.75, textAlign: 'center', cursor: 'pointer', px: 1,
                  opacity: emphasis ? 1 : Math.max(0.15, 0.55 - Math.abs(distance) * 0.15),
                  transition: 'opacity 0.35s ease',
                  '&:hover': { opacity: emphasis ? 1 : 0.75 },
                }}
              >
                {(lang === 'ja' || lang === 'both') && (
                  <Typography
                    variant={emphasis ? 'body2' : 'caption'}
                    sx={{ color: emphasis && lang === 'both' ? 'text.secondary' : 'inherit' }}
                  >
                    {seg.ja}
                  </Typography>
                )}
                {(lang === 'zh' || lang === 'both') && (
                  <Typography
                    variant={emphasis ? 'h6' : 'caption'}
                    sx={{ fontWeight: emphasis ? 700 : 400 }}
                  >
                    {seg.zh}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * 逐字稿同步捲動面板。
 * - 從後端 /programs/:id/segments 取 segments.json（每句帶 start_ms/end_ms/ja/zh）
 * - 綁 <audio> 的 timeupdate，高亮當前句、自動捲動
 * - 點某句 → seek 到該句時間
 * - 語言切換：中文（預設）/ 日文 / 雙語
 * - 子母畫面：用 Document Picture-in-Picture API 把字幕丟到一個永遠置頂的
 *   獨立小視窗，這樣切到別的分頁或別的視窗時，音訊仍在本分頁背景播放，
 *   字幕則顯示在浮動視窗裡（用 `'documentPictureInPicture' in window` 動態偵測支援度，
 *   不寫死瀏覽器名單，之後有更多瀏覽器支援時不用改程式碼）
 *
 * props:
 *   programId  - 節目 id
 *   getPlayer  - () => HTMLAudioElement（可能為 null，播放器還沒 ready）
 */
function TranscriptPanel({ programId, getPlayer }) {
  const theme = useTheme();
  const [segments, setSegments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('zh'); // zh | ja | both
  const [activeIdx, setActiveIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pipWindow, setPipWindow] = useState(null);

  const pipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/programs/${programId}/segments`)
      .then((res) => {
        if (cancelled) return;
        // 只顯示非歌曲段落（is_song 的濾掉）
        const segs = (res.data.segments || []).filter((s) => !s.is_song);
        setSegments(segs);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? '此節目尚無翻譯字幕' : '無法載入字幕');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [programId]);

  // 綁 <audio> 的 timeupdate / play / pause 事件（取代 rAF 輪詢，暫停/背景時不耗 CPU）。
  // 播放器實例可能還沒 ready，用短輪詢等它出現後一次性綁定。
  useEffect(() => {
    if (!segments || segments.length === 0) return;

    let player = null;
    let waitId = null;

    const onTimeUpdate = () => {
      if (!player || typeof player.currentTime !== 'number') return;
      const ms = player.currentTime * 1000;
      // 二分找當前句（segments 依時間排序）
      let lo = 0, hi = segments.length - 1, found = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (ms < segments[mid].start_ms) hi = mid - 1;
        else if (ms > segments[mid].end_ms) lo = mid + 1;
        else { found = mid; break; }
      }
      // 落在兩句之間時，取前一句（lo-1）維持顯示連續
      if (found === -1 && lo > 0) found = lo - 1;
      setActiveIdx((prev) => (prev !== found ? found : prev));
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    const bind = () => {
      player = getPlayer && getPlayer();
      if (player) {
        player.addEventListener('timeupdate', onTimeUpdate);
        player.addEventListener('play', onPlay);
        player.addEventListener('pause', onPause);
        setIsPlaying(!player.paused);
        if (waitId) { clearInterval(waitId); waitId = null; }
      }
    };

    bind();
    if (!player) waitId = setInterval(bind, 250);

    return () => {
      if (waitId) clearInterval(waitId);
      if (player) {
        player.removeEventListener('timeupdate', onTimeUpdate);
        player.removeEventListener('play', onPlay);
        player.removeEventListener('pause', onPause);
      }
    };
  }, [segments, getPlayer]);

  const seekTo = useCallback((ms) => {
    const player = getPlayer && getPlayer();
    if (player) {
      player.currentTime = ms / 1000;
      if (player.paused) player.play();
    }
  }, [getPlayer]);

  const togglePlay = useCallback(() => {
    const player = getPlayer && getPlayer();
    if (!player) return;
    if (player.paused) player.play();
    else player.pause();
  }, [getPlayer]);

  const skip = useCallback((deltaSec) => {
    const player = getPlayer && getPlayer();
    if (!player || typeof player.currentTime !== 'number') return;
    player.currentTime = Math.max(0, player.currentTime + deltaSec);
  }, [getPlayer]);

  // 把主頁面的樣式表同步到子母畫面視窗。MUI/Emotion 在效能模式下是用
  // sheet.insertRule() 直接寫入 CSSOM，不會反映在 <style> 節點的 textContent
  // 上、也不會觸發任何 DOM mutation，所以複製節點文字或監聽 head 的
  // MutationObserver 都補不到這些規則。唯一可靠的方式是直接讀取
  // styleSheet.cssRules（永遠反映當下實際生效的規則），並且用輪詢方式
  // 持續重新同步——子母畫面裡的元件第一次畫出來時可能還會產生新的
  // class，開啟當下同步一次不夠。跨來源樣式表（例如 Google Fonts）讀
  // cssRules 會丟 SecurityError，改成掛一個對應的 <link>。
  const syncStylesInto = (targetDoc, state) => {
    [...document.styleSheets].forEach((sheet) => {
      const sourceNode = sheet.ownerNode;
      if (!sourceNode) return;
      try {
        const cssText = [...sheet.cssRules].map((r) => r.cssText).join('\n');
        let target = state.cloned.get(sourceNode);
        if (!target) {
          target = targetDoc.createElement('style');
          targetDoc.head.appendChild(target);
          state.cloned.set(sourceNode, target);
        }
        if (target.textContent !== cssText) target.textContent = cssText;
      } catch {
        if (sheet.href && !state.linked.has(sheet.href)) {
          const link = targetDoc.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href;
          targetDoc.head.appendChild(link);
          state.linked.add(sheet.href);
        }
      }
    });
  };

  const openPip = async () => {
    if (!pipSupported) return;
    if (pipWindow) {
      pipWindow.close();
      return;
    }
    const pw = await window.documentPictureInPicture.requestWindow({ width: 920, height: 320 });
    const syncState = { cloned: new Map(), linked: new Set() };
    syncStylesInto(pw.document, syncState);
    const syncId = setInterval(() => syncStylesInto(pw.document, syncState), 500);
    pw.document.documentElement.style.colorScheme = theme.palette.mode;
    pw.document.body.style.margin = '0';
    pw.document.body.style.height = '100vh';
    pw.document.body.style.overflow = 'hidden';
    pw.document.title = '逐字稿字幕';
    pw.addEventListener('pagehide', () => {
      clearInterval(syncId);
      setPipWindow(null);
    }, { once: true });
    setPipWindow(pw);
  };

  // 換頁（本元件卸載）時，若子母畫面還開著就一併關閉，避免留下顯示過期內容的視窗
  useEffect(() => () => { if (pipWindow) pipWindow.close(); }, [pipWindow]);

  const langToggle = (size) => (
    <ToggleButtonGroup
      size="small"
      value={lang}
      exclusive
      onChange={(_, v) => v && setLang(v)}
    >
      <ToggleButton value="zh" sx={size === 'small' ? { py: 0.2, px: 1 } : undefined}>中文</ToggleButton>
      <ToggleButton value="ja" sx={size === 'small' ? { py: 0.2, px: 1 } : undefined}>日文</ToggleButton>
      <ToggleButton value="both" sx={size === 'small' ? { py: 0.2, px: 1 } : undefined}>雙語</ToggleButton>
    </ToggleButtonGroup>
  );

  const pipContent = pipWindow && segments && createPortal(
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      bgcolor: 'background.default', color: 'text.primary',
      fontFamily: theme.typography.fontFamily,
    }}
    >
      <Box sx={{
        px: 1, py: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        flexShrink: 0,
      }}
      >
        {langToggle('small')}
      </Box>
      <PipCaption segments={segments} activeIdx={activeIdx} lang={lang} onSeek={seekTo} />
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5,
        py: 1, flexShrink: 0,
      }}
      >
        <IconButton
          onClick={() => skip(-10)}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Replay10Icon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={togglePlay}
          sx={{
            width: 40, height: 40,
            bgcolor: 'primary.main', color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>
        <IconButton
          onClick={() => skip(10)}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Forward10Icon fontSize="small" />
        </IconButton>
      </Box>
    </Box>,
    pipWindow.document.body,
  );

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
      <Box sx={{
        px: 2, py: 1, display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderBottom: 1, borderColor: 'divider',
      }}>
        <Typography variant="subtitle2">逐字稿</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={
            pipSupported
              ? (pipWindow ? '關閉子母畫面' : '在子母畫面顯示字幕（可切到其他分頁/視窗繼續看）')
              : '此瀏覽器目前不支援子母畫面功能'
          }
          >
            <span>
              <IconButton
                size="small"
                onClick={openPip}
                disabled={!pipSupported}
                sx={{
                  color: pipWindow ? 'primary.contrastText' : 'text.secondary',
                  bgcolor: pipWindow ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: pipWindow ? 'primary.dark' : 'action.hover' },
                }}
              >
                <PictureInPictureAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {langToggle()}
        </Box>
      </Box>
      <TranscriptList segments={segments} activeIdx={activeIdx} lang={lang} onSeek={seekTo} />
      {pipContent}
    </Paper>
  );
}

export default TranscriptPanel;
